/**
 * ChatMessageBubble
 * Renders a single chat message with styling based on role.
 * AI messages are rendered with markdown support.
 * Tool result cards render OUTSIDE the bubble as standalone sections.
 * Colors from Figma: AI = forest (#5a5b3f), User = water (#344653)
 */

import type { UIMessage } from "@ai-sdk/react"
import { AlertCircle, Sparkles } from "lucide-react"
import React from "react"
import Markdown from "react-markdown"
import aiAvatarImg from "@/assets/icons/ai-conversation-avatar.png"
import ritualityAiIcon from "@/assets/icons/rituality-ai.svg"
import type {
  CoachToolOutput,
  CoachToolResult,
  InsightToolResult,
  RitualToolOutput,
  RitualToolResult,
} from "../../types/ai-chat.types"
import { CardSkeleton } from "../cards/CardSkeleton"
import { CoachRecommendationCard } from "../cards/CoachRecommendationCard"
import { InsightSavedCard } from "../cards/InsightSavedCard"
import { RitualRecommendationCard } from "../cards/RitualRecommendationCard"

interface ChatMessageBubbleProps {
  message: UIMessage
}

/** Static markdown component overrides -- extracted to module scope to avoid re-creating on each render */
const markdownComponents = {
  p: ({ children }: { children?: React.ReactNode }) => <p className="mb-2 last:mb-0">{children}</p>,
  strong: ({ children }: { children?: React.ReactNode }) => (
    <strong className="font-semibold text-foreground">{children}</strong>
  ),
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul className="mb-2 ml-4 list-disc space-y-1">{children}</ul>
  ),
  ol: ({ children }: { children?: React.ReactNode }) => (
    <ol className="mb-2 ml-4 list-decimal space-y-1">{children}</ol>
  ),
  li: ({ children }: { children?: React.ReactNode }) => (
    <li className="text-foreground">{children}</li>
  ),
  hr: () => <hr className="my-3 border-border" />,
  h3: ({ children }: { children?: React.ReactNode }) => (
    <h3 className="font-semibold text-foreground mb-1">{children}</h3>
  ),
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-[var(--color-chat-link)] underline"
    >
      {children}
    </a>
  ),
}

/** Section header shown above tool result cards */
function ToolSectionHeader({ title, icon }: { title: string; icon?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between px-1 mb-2">
      <div className="flex items-center gap-1.5">
        {icon || <Sparkles className="h-4 w-4 text-foreground/70" />}
        <span className="text-[17px] font-semibold text-foreground">{title}</span>
      </div>
    </div>
  )
}

/** Typed tool part shape used within UIMessage.parts */
interface ToolPart {
  type: string
  state?: string
  output?: unknown
}

/**
 * Groups tool parts by type and renders one section per type with merged results.
 * Prevents duplicate headers when the AI calls the same tool multiple times.
 */
/** Inline error message for tool failures */
function ToolErrorMessage({ message }: { message: string }) {
  return (
    <div className="my-2 flex items-start gap-2 rounded-lg border border-red-500/20 bg-red-500/5 p-3">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" />
      <p className="text-xs text-foreground/70">{message}</p>
    </div>
  )
}

function renderGroupedToolSections(toolParts: ToolPart[]) {
  // Collect all results by tool type
  const rituals: RitualToolResult[] = []
  const coaches: CoachToolResult[] = []
  const insights: InsightToolResult[] = []
  let hasLoadingRituals = false
  let hasLoadingCoaches = false
  let ritualError: string | undefined
  let coachError: string | undefined

  for (const part of toolParts) {
    if (part.type === "tool-searchRituals") {
      if (part.state === "output-available" && part.output) {
        const data = part.output as RitualToolOutput
        if (data.error && (!data.rituals || data.rituals.length === 0)) {
          ritualError = data.error
        } else {
          rituals.push(...data.rituals)
        }
      } else if (part.state === "error") {
        ritualError = "Could not search for rituals right now. Please try again."
      } else {
        hasLoadingRituals = true
      }
    } else if (part.type === "tool-findCoaches") {
      if (part.state === "output-available" && part.output) {
        const data = part.output as CoachToolOutput
        if (data.error && (!data.coaches || data.coaches.length === 0)) {
          coachError = data.error
        } else {
          coaches.push(...data.coaches)
        }
      } else if (part.state === "error") {
        coachError = "Could not find coaches right now. Please try again."
      } else {
        hasLoadingCoaches = true
      }
    } else if (part.type === "tool-saveGrowthInsight") {
      if (part.state === "output-available" && part.output) {
        insights.push(part.output as InsightToolResult)
      }
    }
  }

  return (
    <>
      {/* Ritual recommendations */}
      {hasLoadingRituals && rituals.length === 0 && (
        <div className="mt-3 w-full max-w-[640px]">
          <CardSkeleton label="Searching rituals..." />
        </div>
      )}
      {ritualError && rituals.length === 0 && (
        <div className="mt-3 w-full max-w-[640px]">
          <ToolErrorMessage message="Could not search for rituals right now. Please try again." />
        </div>
      )}
      {rituals.length > 0 && (
        <div className="mt-3 w-full max-w-[640px]">
          <ToolSectionHeader
            title="Recommended Rituals"
            icon={<img src={ritualityAiIcon} alt="" className="h-4 w-4" />}
          />
          <RitualRecommendationCard rituals={rituals} />
        </div>
      )}

      {/* Coach recommendations */}
      {hasLoadingCoaches && coaches.length === 0 && (
        <div className="mt-3 w-full max-w-[640px]">
          <CardSkeleton label="Finding coaches..." />
        </div>
      )}
      {coachError && coaches.length === 0 && (
        <div className="mt-3 w-full max-w-[640px]">
          <ToolErrorMessage message="Could not find coaches right now. Please try again." />
        </div>
      )}
      {coaches.length > 0 && (
        <div className="mt-3 w-full max-w-[640px]">
          <ToolSectionHeader title="Recommended Ritualists" />
          <CoachRecommendationCard coaches={coaches} />
        </div>
      )}

      {/* Growth insights */}
      {insights.map((insight, idx) => (
        <div key={insight.insightId ?? `insight-${idx}`} className="mt-2 w-full max-w-[640px]">
          <InsightSavedCard data={insight} />
        </div>
      ))}
    </>
  )
}

export const ChatMessageBubble = React.memo(function ChatMessageBubble({
  message,
}: ChatMessageBubbleProps) {
  const isUser = message.role === "user"

  if (isUser) {
    const textContent = message.parts
      .filter((part): part is { type: "text"; text: string } => part.type === "text")
      .map(part => part.text)
      .join("")

    return (
      <div className="flex justify-end">
        <div className="max-w-[460px] rounded-[10px] p-[15px] text-[13px] leading-[22px] text-text-primary bg-brand-slate">
          <div className="whitespace-pre-wrap break-words">{textContent}</div>
        </div>
      </div>
    )
  }

  // Separate text parts from tool parts
  const textParts = message.parts.filter(
    (part): part is { type: "text"; text: string } =>
      part.type === "text" && !!(part as { text?: string }).text?.trim(),
  )

  const toolParts = message.parts.filter(part => part.type.startsWith("tool-")) as ToolPart[]

  return (
    <div className="flex flex-col items-start gap-[10px]">
      {/* Text content inside the olive bubble */}
      {textParts.length > 0 && (
        <div className="max-w-[460px] rounded-[10px] p-[15px] text-[13px] leading-[22px] text-text-primary bg-brand-olive">
          {textParts.map((textPart, index) => (
            <div
              key={index}
              className="prose prose-sm prose-invert max-w-none break-words [&>*:first-child]:mt-0 [&>*:last-child]:mb-0"
            >
              <Markdown components={markdownComponents}>{textPart.text}</Markdown>
            </div>
          ))}
        </div>
      )}

      {/* Rituality AI label below bubble */}
      <div className="flex items-center gap-[10px] h-[30px]">
        <img src={aiAvatarImg} alt="" className="size-[30px] rounded-full" />
        <span className="text-[13px] font-semibold text-text-primary">Rituality AI</span>
      </div>

      {/* Tool results rendered OUTSIDE the bubble -- grouped by type, one header each */}
      {toolParts.length > 0 && renderGroupedToolSections(toolParts)}
    </div>
  )
})
